import * as React from 'react';
import { useInView } from 'react-intersection-observer';

import useMedia from 'use-media';

import { theme } from '@notino/react-styleguide';

import { TabIds } from '@containers/ProductDetailContainer/ProductDetail/model';
import { useTrackingContext } from '@context/tracking/TrackingContext';
import { dispatchTrackingEvent } from '@context/tracking/utils';

import { useNewPdDesignEnabled } from '../../containers/ProductDetailContainer/ProductDetail/hooks/useNewPdDesignEnabled';

import { useTabSwitcher } from './context/TabSwitcherContext';
import { ITabContentProps } from './model';
import { ChildrenWrapper, TabDivider, TitleWrapper } from './styled';
import { TabTitle } from './TabTitle';

export const TabContent: React.FC<ITabContentProps> = ({
  isDefaultTab,
  children,
  tabId,
  title,
  trackingLabel,
  lazyLoad,
  tabDividerMobile = true,
  tabIdentifier,
  isDefaultTabOpen,
  titleHiddenOnDesktop = false,
}) => {
  const {
    activeTabId,
    isReviewTabVisibleOnMobile,
    toggleIsReviewTabVisibleOnMobile,
  } = useTabSwitcher();
  const { getTimeFromInit } = useTrackingContext();
  const newPdDesignEnabled = useNewPdDesignEnabled();
  const [ref, inView] = useInView({ triggerOnce: true });

  const [isOpen, toggleOpen] = React.useReducer(
    (prev) => !prev,
    isDefaultTabOpen ? isDefaultTab : false
  );

  const isDesktop = useMedia(`(min-width: ${theme.breakpoints.md}rem)`);

  const handleOpen = React.useCallback(
    (e: React.SyntheticEvent<HTMLElement> | React.KeyboardEvent) => {
      e.stopPropagation();

      if (trackingLabel) {
        dispatchTrackingEvent({
          event: 'subpage_view',
          subpage: {
            type: 'tab',
            interaction: 'click',
            action: 'click_on_tab',
            name: trackingLabel,
            timing: getTimeFromInit(),
          },
          _clear: true,
        });
      }

      if (tabId === TabIds.Reviews) {
        toggleIsReviewTabVisibleOnMobile();
      }

      toggleOpen();
    },
    [trackingLabel, tabId, getTimeFromInit, toggleIsReviewTabVisibleOnMobile]
  );

  const isActive: boolean =
    (typeof activeTabId === 'undefined' && isDefaultTab) ||
    activeTabId === tabId;

  const isExpanded =
    tabId === TabIds.Reviews ? isReviewTabVisibleOnMobile : isOpen;

  const childrenComponent =
    typeof children === 'function'
      ? children({ tabId, activeTabId })
      : children;

  const isTabExpanded = isDesktop ? isActive : isExpanded;
  return (
    <>
      {title && (
        <>
          <TabDivider
            newPdDesignEnabled={newPdDesignEnabled}
            tabDividerMobile={tabDividerMobile}
            tabDividerDesktop={isActive}
            hideOnDesktop={titleHiddenOnDesktop}
          />
          <TitleWrapper
            isActive={isActive}
            newPdDesignEnabled={newPdDesignEnabled}
            hideOnDesktop={titleHiddenOnDesktop}
          >
            <TabTitle
              isExpanded={isExpanded}
              title={title}
              handleExpand={handleOpen}
              role={isDesktop ? 'heading' : 'button'}
              aria-controls={tabIdentifier}
              aria-expanded={isExpanded}
              tabIndex={isDesktop ? -1 : 0}
              newPdDesignEnabled={newPdDesignEnabled}
            />
          </TitleWrapper>
        </>
      )}
      <div
        role={'tabpanel'}
        tabIndex={isTabExpanded ? 0 : -1}
        aria-hidden={!isTabExpanded}
        id={tabIdentifier}
      >
        <ChildrenWrapper
          ref={ref}
          isExpanded={isExpanded}
          isActive={isActive}
          newPdDesignEnabled={newPdDesignEnabled}
        >
          {lazyLoad ? inView && childrenComponent : childrenComponent}
        </ChildrenWrapper>
        <TabDivider
          tabDividerMobile={true}
          newPdDesignEnabled={newPdDesignEnabled}
        />
      </div>
    </>
  );
};
